import { useContext, useEffect, useRef } from "react"
import { useSelector } from "react-redux"
import { State } from "../../redux/reducers"
import { TreeContext } from "../contexts/organizeTreeBibleContexts"
import { LockContext } from "../contexts/treeLockContext"
import { defaultQuery } from "../models/Query"
import { TreeLockDto } from "../models/TreeModels"
import { getAuthorizationHeaders, query } from "../utils/query"

const HEARTBEAT_INTERVAL_MS = 90_000

const getLockStatus = async (treeId: number, token: string | undefined) => {
  let result = { ...defaultQuery }
  await query<TreeLockDto>({
    url: `/api/kyrielle/tree/${treeId}/lock`,
    method: "get",
    headers: getAuthorizationHeaders(token),
    setter: (update) => {
      result = typeof update === "function" ? update(result) : update
    },
  })
  return result
}

/**
 * Hook singleton — à appeler UNE SEULE FOIS dans la page (OrganizeTree).
 * Gère le cycle de vie du verrou :
 *  - état initial à chaque chargement d'un CDF (GET /lock)
 *  - heartbeat tant que le verrou est à nous
 *  - libération sur unmount / changement d'onglet / fermeture navigateur
 */
export const useTreeLockLifecycle = () => {
  const [tree] = useContext(TreeContext)
  const [lockState, setLockState] = useContext(LockContext)
  const token = useSelector(({ auth }: State) => auth.token)
  const tokenRef = useRef(token)

  useEffect(() => { tokenRef.current = token }, [token])

  const treeId = tree.data?.id

  useEffect(() => {
    if (!treeId) {
      setLockState({ status: "free" })
      return
    }
    let cancelled = false
    getLockStatus(treeId, tokenRef.current).then((result) => {
      if (cancelled) return
      if (result.statusCode === 204) {
        setLockState({ status: "free" })
      } else if (result.statusCode === 200 && result.data) {
        if (result.data.isOwner) {
          setLockState({ status: "mine" })
        } else {
          setLockState({ status: "other", ownerName: result.data.ownerName })
        }
      }
    })
    return () => { cancelled = true }
  }, [treeId])

  useEffect(() => {
    if (!treeId || lockState.status !== "mine") return

    const heartbeat = setInterval(async () => {
      const result = await getLockStatus(treeId, tokenRef.current)

      if (result.statusCode === 204) {
        setLockState({ status: "free" })
        return
      }
      if (result.statusCode === 200 && result.data) {
        if (result.data.isOwner) {
          query<void>({
            url: `/api/kyrielle/tree/${treeId}/lock`,
            method: "post",
            headers: getAuthorizationHeaders(tokenRef.current),
            setter: () => {},
          })
        } else {
          setLockState({ status: "other", ownerName: result.data.ownerName })
        }
      }
    }, HEARTBEAT_INTERVAL_MS)

    const release = () => {
      query<void>({
        url: `/api/kyrielle/tree/${treeId}/lock`,
        method: "delete",
        headers: getAuthorizationHeaders(tokenRef.current),
        setter: () => {},
      })
    }

    window.addEventListener("beforeunload", release)

    return () => {
      clearInterval(heartbeat)
      window.removeEventListener("beforeunload", release)
      release()
    }
  }, [lockState.status, treeId])
}


export const useAcquireLock = () => {
  const [tree] = useContext(TreeContext)
  const [, setLockState] = useContext(LockContext)
  const token = useSelector(({ auth }: State) => auth.token)

  return async (): Promise<boolean> => {
    const treeId = tree.data?.id
    if (!treeId) return false

    const checkResult = await getLockStatus(treeId, token)

    if (checkResult.statusCode === 200 && checkResult.data) {
      if (checkResult.data.isOwner) {
        setLockState({ status: "mine" })
        return true
      }
      setLockState({ status: "other", ownerName: checkResult.data.ownerName })
      return false
    }

    let acquireResult = { ...defaultQuery }
    await query<TreeLockDto>({
      url: `/api/kyrielle/tree/${treeId}/lock`,
      method: "post",
      headers: getAuthorizationHeaders(token),
      setter: (update) => {
        acquireResult = typeof update === "function" ? update(acquireResult) : update
      },
    })

    if (acquireResult.statusCode === 200) {
      setLockState({ status: "mine" })
      return true
    }
    return false
  }
}

export const useReleaseLock = () => {
  const [tree] = useContext(TreeContext)
  const [lockState, setLockState] = useContext(LockContext)
  const token = useSelector(({ auth }: State) => auth.token)

  return async (): Promise<void> => {
    if (lockState.status !== "mine") return
    const treeId = tree.data?.id
    if (!treeId) return

    await query<void>({
      url: `/api/kyrielle/tree/${treeId}/lock`,
      method: "delete",
      headers: getAuthorizationHeaders(token),
      setter: () => {},
    })
    setLockState({ status: "free" })
  }
}
